import { useState, useRef, useCallback } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { Sidebar } from './Sidebar';
import { JumpDialog, useJumpDialog } from '@/components/ui';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const routerState = useRouterState();
  const isLoading = routerState.status === 'pending';
  const jumpDialog = useJumpDialog();
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Close sidebar and return focus to toggle button
  const handleSidebarClose = useCallback(() => {
    setSidebarOpen(false);
    // Return focus to menu button after sidebar closes
    requestAnimationFrame(() => {
      menuButtonRef.current?.focus();
    });
  }, []);

  return (
    <div className="min-h-screen bg-bg">
      {/* Skip to content link - visible on focus for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-primary focus:text-bg focus:rounded-lg focus:shadow-lg focus:outline-none"
      >
        Skip to content
      </a>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-bg/80 backdrop-blur-sm lg:hidden"
          onClick={handleSidebarClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={handleSidebarClose} />

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b border-border bg-surface px-4 lg:px-6">
          <button
            ref={menuButtonRef}
            type="button"
            className="btn-ghost p-2 lg:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
            aria-expanded={sidebarOpen}
            aria-controls="mobile-sidebar"
          >
            <MenuIcon />
          </button>

          {/* Jump to button */}
          <button
            type="button"
            className="hidden lg:flex items-center gap-2 px-3 py-1.5 text-sm text-muted bg-bg border border-border rounded-lg hover:border-primary/50 transition-colors duration-fast"
            onClick={jumpDialog.open}
            aria-label="Jump to page (Cmd+K)"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
            <span>Jump to…</span>
            <kbd className="text-xs bg-surface px-1.5 py-0.5 rounded border border-border">⌘K</kbd>
          </button>

          {/* Loading indicator */}
          {isLoading && (
            <div className="h-1 absolute bottom-0 left-0 right-0 bg-primary/20">
              <div className="h-full w-1/3 bg-primary animate-pulse" />
            </div>
          )}
        </header>

        {/* Page content */}
        <main id="main-content" className="p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Jump dialog */}
      <JumpDialog isOpen={jumpDialog.isOpen} onClose={jumpDialog.close} />
    </div>
  );
}

function MenuIcon() {
  return (
    <svg
      className="h-5 w-5 text-text"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
      />
    </svg>
  );
}
